import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import SchoolAvatar from './SchoolAvatar';
import { getApiBaseUrl } from '../apiBase';

const PREVIEW_SIZE = 120;

export default function SchoolLogoUpload({ schoolId, logoPath, onUpdated, onError }) {
    const { t } = useTranslation('school');
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [localLogo, setLocalLogo] = useState(logoPath);
    const [cacheBust, setCacheBust] = useState(0);

    useEffect(() => {
        setLocalLogo(logoPath);
    }, [logoPath]);

    const displayLogo = previewBlob || localLogo;

    async function handleFile(e) {
        const file = e.target.files?.[0];
        if (!file || !schoolId) return;

        if (!file.type.startsWith('image/')) {
            onError?.(t('logoUpload.invalidType'));
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            onError?.(t('logoUpload.maxSize'));
            return;
        }

        const objectUrl = URL.createObjectURL(file);
        setPreviewBlob(objectUrl);
        setUploading(true);

        const form = new FormData();
        form.append('file', file);

        const token = localStorage.getItem('token');

        try {
            const res = await axios.post(
                `${getApiBaseUrl()}/school/${schoolId}/logo`,
                form,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            const path = res.data.logo;
            setPreviewBlob(null);
            URL.revokeObjectURL(objectUrl);
            setLocalLogo(path);
            setCacheBust(Date.now());
            onUpdated?.(path);
        } catch (err) {
            setPreviewBlob(null);
            URL.revokeObjectURL(objectUrl);
            onError?.(err.response?.data?.message || t('logoUpload.uploadFailed'));
        }
        setUploading(false);
        if (inputRef.current) inputRef.current.value = '';
    }

    async function handleRemove() {
        if (!schoolId || !window.confirm(t('logoUpload.confirmRemove'))) return;
        setUploading(true);
        const token = localStorage.getItem('token');
        try {
            await axios.delete(`${getApiBaseUrl()}/school/${schoolId}/logo`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPreviewBlob(null);
            setLocalLogo(null);
            setCacheBust(Date.now());
            onUpdated?.(null);
        } catch (err) {
            onError?.(err.response?.data?.message || t('logoUpload.deleteFailed'));
        }
        setUploading(false);
    }

    return (
        <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '10px' }}>
                {t('logoUpload.label')}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', width: PREVIEW_SIZE, height: PREVIEW_SIZE }}>
                    <SchoolAvatar
                        logoPath={displayLogo}
                        cacheBust={previewBlob ? undefined : cacheBust}
                        size={PREVIEW_SIZE}
                        alt={t('logoUpload.avatarAlt')}
                        style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}
                    />
                    <button
                        type="button"
                        disabled={uploading}
                        onClick={() => inputRef.current?.click()}
                        title={t('logoUpload.pickPhoto')}
                        style={{
                            position: 'absolute',
                            right: 0,
                            bottom: 0,
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            border: '3px solid white',
                            background: uploading ? '#94a3b8' : '#EB3333',
                            color: 'white',
                            fontSize: '18px',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
                        }}
                    >
                        📷
                    </button>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        style={{ display: 'none' }}
                        onChange={handleFile}
                    />
                </div>
                <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.5, maxWidth: '280px' }}>
                    {t('logoUpload.hint')}
                    {uploading && <div style={{ marginTop: '8px', color: '#EB3333' }}>{t('logoUpload.uploading')}</div>}
                    {localLogo && !uploading && (
                        <button
                            type="button"
                            onClick={handleRemove}
                            style={{
                                display: 'block', marginTop: '10px', padding: 0,
                                border: 'none', background: 'none', color: '#c62828',
                                cursor: 'pointer', fontSize: '13px', textDecoration: 'underline'
                            }}
                        >
                            {t('logoUpload.removePhoto')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
